import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import HeaderNav from '../HeaderNav';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  BellAlertIcon,
  BookmarkIcon,
  CalendarDaysIcon,
  CheckIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import ConversationCard from '../common/ConversationCard';
import ProposalCard from '../cashflow/ProposalCard';
import ReminderEditor from './ReminderEditor';

const MyPlanPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('due');
  const [savedItems, setSavedItems] = useState([]);
  const [dueReminders, setDueReminders] = useState([]);
  const [nudgeProposals, setNudgeProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarUrl, setCalendarUrl] = useState('');
  const [editor, setEditor] = useState(null);
  const [expandedId, setExpandedId] = useState(searchParams.get('saved') || null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [savedRes, dueRes, calRes, inboxRes] = await Promise.all([
        axios.get('/api/engagement/saved'),
        axios.get('/api/engagement/reminders/inbox'),
        axios.get('/api/engagement/calendar/url').catch(() => ({ data: {} })),
        axios.get('/api/engagement/inbox').catch(() => ({ data: {} })),
      ]);
      setSavedItems(savedRes.data?.saved_items || []);
      setDueReminders(dueRes.data?.reminders || []);
      setNudgeProposals(inboxRes.data?.nudges || []);
      setCalendarUrl(calRes.data?.feed_url || '');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const savedId = searchParams.get('saved');
    if (savedId) {
      setExpandedId(savedId);
      setTab('saved');
    }
  }, [searchParams]);

  const completeReminder = async (id) => {
    await axios.post(`/api/engagement/reminders/${id}/complete`);
    load();
  };

  const snoozeReminder = async (id, days = 1) => {
    const until = new Date();
    until.setDate(until.getDate() + days);
    await axios.post(`/api/engagement/reminders/${id}/snooze`, {
      until: until.toISOString(),
    });
    load();
  };

  const deleteReminder = async (id) => {
    if (!window.confirm('Delete this reminder?')) return;
    await axios.delete(`/api/engagement/reminders/${id}`);
    load();
  };

  const renderSavedCard = (item) => {
    const snap = item.card_snapshot;
    if (!snap) {
      return (
        <div className="text-body text-secondary">
          <p className="font-medium text-primary">{item.title}</p>
          <p className="mt-1 whitespace-pre-wrap">{item.content}</p>
        </div>
      );
    }
    return (
      <ConversationCard
        card={snap}
        index={0}
        user={user}
        section={item.source_surface || 'saved'}
      />
    );
  };

  const formatDue = (iso) =>
    iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : 'now';

  return (
    <div className="min-h-screen bg-bone">
      <HeaderNav
        activeSection="my-plan"
        onSectionChange={() => {}}
        user={user}
        onLogout={onLogout}
        visionCount={0}
      />
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-10">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-secondary hover:text-primary mb-6 text-body transition-colors duration-300"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back
        </button>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-8 pb-6 border-b border-hairline">
          <div>
            <h1 className="font-display text-h1 text-primary">My plan</h1>
            <p className="text-body text-secondary mt-2 max-w-xl">
              Saved advice, your reminders, and optional Aura event suggestions.
            </p>
          </div>
          {dueReminders.length > 0 && (
            <span className="text-label text-pine border border-pine rounded-md px-2.5 py-1 bg-pine-tint">
              {dueReminders.length} reminder{dueReminders.length === 1 ? '' : 's'} due
            </span>
          )}
        </div>

        <div className="flex gap-6 mb-6 border-b border-hairline">
          <button
            type="button"
            onClick={() => setTab('due')}
            className={`flex items-center gap-2 pb-3 text-h3 font-medium transition-colors duration-300 ${
              tab === 'due' ? 'nav-tab-active' : 'nav-tab'
            }`}
          >
            <BellAlertIcon className="w-4 h-4" />
            Due ({dueReminders.length + nudgeProposals.length})
          </button>
          <button
            type="button"
            onClick={() => setTab('saved')}
            className={`flex items-center gap-2 pb-3 text-h3 font-medium transition-colors duration-300 ${
              tab === 'saved' ? 'nav-tab-active' : 'nav-tab'
            }`}
          >
            <BookmarkIcon className="w-4 h-4" />
            Saved ({savedItems.length})
          </button>
        </div>

        {calendarUrl && (
          <div className="mb-6 py-4 px-4 border border-hairline rounded-lg bg-bone-raised flex flex-wrap items-center gap-3">
            <CalendarDaysIcon className="w-5 h-5 text-pine shrink-0" />
            <span className="text-body text-secondary flex-1 min-w-[12rem]">
              Calendar feed for reminders with calendar enabled
            </span>
            <a
              href={calendarUrl}
              className="btn-secondary text-label py-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              Add to calendar
            </a>
          </div>
        )}

        {loading ? (
          <p className="text-body text-secondary">Loading…</p>
        ) : tab === 'due' ? (
          dueReminders.length === 0 && nudgeProposals.length === 0 ? (
            <p className="text-body text-secondary">
              Nothing due. Save advice from chat, set a reminder, or check back after Aura surfaces
              an event suggestion.
            </p>
          ) : (
            <div className="space-y-8">
              {dueReminders.length > 0 && (
                <section>
                  <h2 className="font-display text-h2 text-primary mb-4">Your reminders</h2>
                  <ul className="space-y-4">
                    {dueReminders.map((rem) => (
                      <motion.li
                        key={rem.id}
                        className="surface-panel p-5 border-l-2 border-l-pine"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                      >
                        <div className="flex items-center gap-2 text-label text-secondary mb-3">
                          <ClockIcon className="w-4 h-4 text-pine" />
                          <span>
                            Due <span className="figure text-primary">{formatDue(rem.next_due)}</span>
                          </span>
                          {rem.note && (
                            <span className="text-faint">— {rem.note}</span>
                          )}
                        </div>
                        {rem.saved_item && (
                          <div className="mb-4 pointer-events-none opacity-95">
                            {renderSavedCard(rem.saved_item)}
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => completeReminder(rem.id)}
                            className="btn-primary text-label py-2 flex items-center gap-1"
                          >
                            <CheckIcon className="w-3.5 h-3.5" />
                            Mark done
                          </button>
                          <button
                            type="button"
                            onClick={() => snoozeReminder(rem.id, 1)}
                            className="btn-secondary text-label py-2"
                          >
                            Snooze 1 day
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setEditor({ savedItemId: rem.saved_item_id, reminder: rem })
                            }
                            className="btn-ghost text-label py-2 flex items-center gap-1"
                          >
                            <PencilIcon className="w-3.5 h-3.5" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteReminder(rem.id)}
                            className="btn-danger text-label py-2 flex items-center gap-1"
                          >
                            <TrashIcon className="w-3.5 h-3.5" />
                            Delete
                          </button>
                        </div>
                      </motion.li>
                    ))}
                  </ul>
                </section>
              )}

              {nudgeProposals.length > 0 && (
                <section>
                  <h2 className="font-display text-h2 text-primary mb-1">Aura event suggestions</h2>
                  <p className="text-body text-secondary mb-4">
                    Proactive nudges (not your reminders). Accept or decline on Cashflow.
                  </p>
                  <div className="space-y-3">
                    {nudgeProposals.map((p) => (
                      <ProposalCard key={p.id} proposal={p} onResolved={() => load()} />
                    ))}
                  </div>
                  <Link
                    to="/cashflow"
                    className="inline-block mt-3 text-body text-pine hover:text-pine-deep"
                  >
                    Open Cashflow
                  </Link>
                </section>
              )}
            </div>
          )
        ) : savedItems.length === 0 ? (
          <p className="text-body text-secondary">
            Nothing saved yet. Use Save on any Aura advice card in chat or cashflow.
          </p>
        ) : (
          <ul className="space-y-4 hairline-list">
            {savedItems.map((item) => (
              <motion.li
                key={item.saved_item_id || item.note_id}
                className={`surface-panel p-5 ${
                  expandedId === item.saved_item_id ? 'border-t-2 border-t-pine' : ''
                }`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              >
                <div className="flex justify-between items-start gap-2 mb-3">
                  <p className="text-label text-secondary figure">
                    {item.source_surface}
                    {item.created_at
                      ? ` · ${new Date(item.created_at).toLocaleDateString()}`
                      : ''}
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      setEditor({ savedItemId: item.saved_item_id || item.note_id })
                    }
                    className="btn-ghost text-label py-1 shrink-0"
                  >
                    Remind me
                  </button>
                </div>
                <button
                  type="button"
                  className="w-full text-left"
                  onClick={() =>
                    setExpandedId(
                      expandedId === item.saved_item_id ? null : item.saved_item_id
                    )
                  }
                >
                  {expandedId === item.saved_item_id ? (
                    renderSavedCard(item)
                  ) : (
                    <div>
                      <p className="font-display text-h3 text-primary">{item.title}</p>
                      <p className="text-body text-secondary line-clamp-2 mt-1">{item.content}</p>
                    </div>
                  )}
                </button>
              </motion.li>
            ))}
          </ul>
        )}
      </div>

      {editor && (
        <ReminderEditor
          savedItemId={editor.savedItemId}
          reminder={editor.reminder}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default MyPlanPage;
